import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { brand, gray } from "../../../theme/themePrimitives";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

interface PdfPageNavigationProps {
  currentPage: number;
  numPages: number;
  canGoNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  requiresAcknowledgement?: boolean;
}

export const PdfPageNavigation: React.FC<PdfPageNavigationProps> = ({
  currentPage,
  numPages,
  canGoNext,
  onPrev,
  onNext,
  requiresAcknowledgement,
}) => {
  const prevEnabled = currentPage > 1;
  const nextEnabled = canGoNext && currentPage < numPages;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: { xs: "12px 16px", md: "16px 32px" },
        borderTop: `1px solid ${gray[200]}`,
      }}
    >
      {/* Compact circular icon button on mobile/tablet */}
      <IconButton
        onClick={onPrev}
        disabled={!prevEnabled}
        aria-label="Previous page"
        sx={{
          display: { xs: "flex", md: "none" },
          backgroundColor: prevEnabled ? brand[600] : gray[50],
          border: `1px solid ${prevEnabled ? brand[600] : gray[200]}`,
          color: prevEnabled ? "white" : gray[400],
          width: 40,
          height: 40,
          ":hover": {
            backgroundColor: prevEnabled ? brand[700] : gray[50],
          },
        }}
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onPrev}
        disabled={!prevEnabled}
        sx={{
          display: { xs: "none", md: "inline-flex" },
          backgroundColor: prevEnabled ? brand[600] : gray[50],
          border: `1px solid ${prevEnabled ? brand[600] : gray[200]}`,
          p: "10px 16px",
          color: prevEnabled ? "white" : gray[400],
          borderRadius: "12px",
          ":hover": {
            backgroundColor: prevEnabled ? brand[700] : gray[50],
          },
        }}
      >
        Previous
      </Button>
      <Stack>
        <Typography
          fontSize="16px"
          fontWeight={600}
          sx={{ textAlign: "center", color: gray[600] }}
        >
          {currentPage} / {numPages}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap={0.5}
        >
          <LockOutlinedIcon fontSize="small" sx={{ color: gray[400] }} />
          <Typography fontSize="14px" sx={{ color: gray[400] }}>
            {requiresAcknowledgement
              ? "View all pages to unlock acknowledgement and complete."
              : "View all pages to complete the assignment."}
          </Typography>
        </Stack>
      </Stack>
      {/* Compact circular icon button on mobile/tablet */}
      <IconButton
        onClick={onNext}
        disabled={!nextEnabled}
        aria-label="Next page"
        sx={{
          display: { xs: "flex", md: "none" },
          backgroundColor: nextEnabled ? brand[600] : gray[50],
          border: `1px solid ${nextEnabled ? brand[600] : gray[200]}`,
          color: nextEnabled ? "white" : gray[400],
          width: 40,
          height: 40,
          ":hover": {
            backgroundColor: nextEnabled ? brand[700] : gray[50],
          },
        }}
      >
        <ArrowForwardIcon fontSize="small" />
      </IconButton>
      <Button
        endIcon={<ArrowForwardIcon />}
        onClick={onNext}
        disabled={!nextEnabled}
        sx={{
          display: { xs: "none", md: "inline-flex" },
          backgroundColor: nextEnabled ? brand[600] : gray[50],
          border: `1px solid ${nextEnabled ? brand[600] : gray[200]}`,
          p: "10px 16px",
          color: nextEnabled ? "white" : gray[400],
          borderRadius: "12px",
          ":hover": {
            backgroundColor: nextEnabled ? brand[700] : gray[50],
          },
        }}
      >
        Next page
      </Button>
    </Box>
  );
};
